"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { modal, header, content } from './styles.js';
import { IconButton } from '../IconButton/IconButton.web.js';
import { usePrevMounted } from '../utils/hooks/usePrevMounted.web.js';
import { Portal } from '../utils/components/Portal/Portal.web.js';
import { IconCross } from '../icons/ui_control/IconCross.web.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { ThemeProvider } from 'styled-components';
import { inAnimationFrame } from '../utils/helpers/events.web.js';
import { Overlay } from '../utils/components/Overlay/Overlay.web.js';
import { TrapFocus } from '../utils/components/TrapFocus/TrapFocus.web.js';
import { renderComponentOrElement } from '../utils/helpers/component.js';

var _excluded = ["noPadding", "hideCloseButton", "headerContent", "style", "children", "fullScreen", "opened", "onClose", "preventClose", "container", "closeButtonAriaLabel", "variant", "aria-labelledby"];
var Modal = function Modal(_ref) {
  var _ref$noPadding = _ref.noPadding,
    noPadding = _ref$noPadding === undefined ? false : _ref$noPadding,
    _ref$hideCloseButton = _ref.hideCloseButton,
    hideCloseButton = _ref$hideCloseButton === undefined ? false : _ref$hideCloseButton,
    headerContent = _ref.headerContent,
    style = _ref.style,
    children = _ref.children,
    fullScreen = _ref.fullScreen,
    opened = _ref.opened,
    onClose = _ref.onClose,
    preventClose = _ref.preventClose,
    container = _ref.container,
    _ref$closeButtonAriaL = _ref.closeButtonAriaLabel,
    closeButtonAriaLabel = _ref$closeButtonAriaL === undefined ? 'Close modal' : _ref$closeButtonAriaL,
    variantProp = _ref.variant,
    ariaLabelledby = _ref['aria-labelledby'],
    props = _objectWithoutProperties(_ref, _excluded);
  var _usePrevMounted = usePrevMounted(),
    _usePrevMounted2 = _slicedToArray(_usePrevMounted, 1),
    prevMounted = _usePrevMounted2[0];
  var theme = useTheme();
  var contentRef = React.useRef(null);
  var headerRef = React.useRef(null);
  var variant = variantProp || theme.variant;
  var handleClose = React.useCallback(function () {
    if (onClose && !preventClose) {
      onClose();
    }
  }, [onClose, preventClose]);
  var animateIn = opened && prevMounted;
  React.useLayoutEffect(function () {
    var current = contentRef.current;
    if (!current || !opened) {
      return;
    }
    var listener = function listener() {
      if (headerRef.current) {
        headerRef.current.dataset.scrolled = current.scrollTop > 0 ? 'true' : 'false';
      }
    };
    var _inAnimationFrame = inAnimationFrame(listener),
      _inAnimationFrame2 = _slicedToArray(_inAnimationFrame, 2),
      debounceListener = _inAnimationFrame2[0],
      cancelDebounce = _inAnimationFrame2[1];
    current.addEventListener('scroll', debounceListener, {
      passive: true
    });
    return function () {
      current.removeEventListener('scroll', listener);
      cancelDebounce();
    };
  }, [contentRef, opened]);
  return /*#__PURE__*/React.createElement(Portal, {
    container: container
  }, /*#__PURE__*/React.createElement(Overlay, {
    "data-testid": "elemental-Modal",
    onClose: handleClose,
    opened: Boolean(opened),
    centered: true
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(TrapFocus, {
    enabled: Boolean(opened)
  }, /*#__PURE__*/React.createElement(modal, _extends({
    $animateIn: animateIn,
    $fullScreen: fullScreen,
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": ariaLabelledby,
    $noPadding: noPadding,
    style: style,
    theme: theme
  }, props), /*#__PURE__*/React.createElement(header, {
    "data-testid": "elemental-Modal-header",
    "data-scrolled": false,
    ref: headerRef
  }, headerContent ? renderComponentOrElement(headerContent, {}) : !preventClose && !hideCloseButton && /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": closeButtonAriaLabel,
    "data-testid": "elemental-Modal-close",
    color: theme.colors.background,
    status: "plain",
    invert: true,
    onClick: handleClose,
    icon: IconCross,
    alignSelf: "flex-end"
  })), /*#__PURE__*/React.createElement(content, {
    "data-testid": "elemental-Modal-content",
    $fullScreen: fullScreen,
    $noPadding: noPadding,
    $alignContent: props.alignContent,
    $variant: variant,
    ref: contentRef
  }, children))))));
};

export { Modal };
